import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cn } from '../lib/utils';

/** DESIGN.md §6 Buttons. Hover feedback is opacity (filled) or colour shift (outline). */
export type ButtonVariant =
  'primary' | 'secondary' | 'icon' | 'ghost-icon' | 'destructive' | 'destructive-outline' | 'brand';

/**
 * `md` is the DESIGN.md `h-9`. `lg` (`h-11`, the login control height) is used for touch
 * surfaces in the cashier, service and kitchen interfaces, where 44px targets matter.
 */
export type ButtonSize = 'md' | 'lg';

const base =
  'inline-flex shrink-0 items-center justify-center gap-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors transition-opacity disabled:cursor-not-allowed disabled:opacity-60 [&_svg]:shrink-0';

const variants: Record<ButtonVariant, string> = {
  primary: 'bg-[var(--accent)] px-4 text-white hover:opacity-90',
  secondary:
    'border border-[var(--line)] bg-[var(--surface)] px-4 text-[var(--ink-soft)] hover:text-[var(--ink)]',
  icon: 'border border-[var(--line)] bg-[var(--surface)] text-[var(--ink-soft)] hover:text-[var(--ink)]',
  'ghost-icon': 'text-[var(--ink-soft)] hover:bg-black/[0.05] hover:text-[var(--ink)]',
  destructive: 'bg-[var(--negative)] px-4 text-white hover:opacity-90',
  'destructive-outline':
    'border border-[var(--line)] bg-[var(--surface)] px-4 text-[var(--negative)] hover:border-[var(--negative)]',
  brand: 'w-full bg-[var(--brand)] font-semibold text-[var(--brand-ink)] hover:opacity-90',
};

const heights: Record<ButtonSize, string> = { md: 'h-9', lg: 'h-11' };
const squares: Record<ButtonSize, string> = { md: 'h-9 w-9', lg: 'h-11 w-11' };

export function buttonClass(
  variant: ButtonVariant = 'secondary',
  size: ButtonSize = 'md',
  className?: string,
): string {
  const isIcon = variant === 'icon' || variant === 'ghost-icon';
  const sizeClass = variant === 'brand' ? 'h-11' : isIcon ? squares[size] : heights[size];
  return cn(base, variants[variant], sizeClass, className);
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', size = 'md', className, type = 'button', ...props },
  ref,
) {
  return (
    <button ref={ref} type={type} className={buttonClass(variant, size, className)} {...props} />
  );
});
